import { useState, type ButtonHTMLAttributes } from 'react';
import { Button, type ButtonVariant } from '../Button';
import { variantColors } from '../Button/variantColors';
import { SkinsButton } from '../SkinsButton';
import { createResponsiveTextStroke } from '@/styles/textStroke';

export interface GameModeCardProps {
  variant?: ButtonVariant;
  onSkinsClick?: () => void;
  showNewBadge?: boolean;
  title: string;
  subtitle?: string;
  subtitleIcon?: React.ReactNode;
  centerButtonProps?: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>;
  rightContent?: React.ReactNode;
  rightButtonProps?: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>;
  showArrow?: boolean;
  className?: string;
}

function ArrowIndicator({ color = '#F59E0B' }: { color?: string }) {
  return (
    <div className="absolute -top-[22%] left-1/2 -translate-x-1/2 w-[40%]">
      <svg viewBox="0 0 72 63" fill="none" className="w-full h-auto">
        {/* Pentagon badge arrow pointing down - matches Figma design */}
        <path
          d="M4 2H68L70 20L36 61L2 20L4 2Z"
          fill={color}
          stroke="#000"
          strokeWidth="4"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

// Inner pill button for the title
function TitlePill({ title, variant }: { title: string; variant: ButtonVariant }) {
  const colors = variantColors[variant];

  return (
    <div className="relative w-[92%] h-[55%]">
      {/* Shadow at bottom */}
      <div
        className="absolute inset-0 top-[8%] rounded-[10px]"
        style={{ background: colors.shadow }}
      />
      {/* Main solid color pill */}
      <div
        className="absolute inset-0 bottom-[8%] rounded-[10px]"
        style={{ background: colors.gradient.end }}
      />
      <span
        className="absolute inset-0 flex items-center justify-center font-mind-boggle text-[#F2F2F2] leading-none"
        style={{
          fontSize: 'clamp(1.5rem, 14cqw, 4rem)',
          fontWeight: 1000,
          ...createResponsiveTextStroke(1, 0.06)
        }}
      >
        {title}
      </span>
    </div>
  );
}

// Center button with proper rounded corners for wide aspect ratio
function CenterButton({
  variant,
  title,
  subtitle,
  subtitleIcon,
  ...props
}: {
  variant: ButtonVariant;
  title: string;
  subtitle?: string;
  subtitleIcon?: React.ReactNode;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>) {
  const [isPressed, setIsPressed] = useState(false);
  const colors = variantColors[variant];
  const activePress = isPressed && !props.disabled;

  return (
    <button
      className={`relative flex-1 transition-all duration-150 ${props.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      style={{ containerType: 'size' }}
      onMouseDown={() => {
        setIsPressed(true);
      }}
      onMouseUp={() => {
        setIsPressed(false);
      }}
      onMouseLeave={() => {
        setIsPressed(false);
      }}
      onTouchStart={() => {
        setIsPressed(true);
      }}
      onTouchEnd={() => {
        setIsPressed(false);
      }}
      {...props}
    >
      {/* Outer wrapper with black border */}
      <div
        className="absolute inset-0 rounded-[20px] border-[3px] border-black overflow-hidden transition-transform duration-150"
        style={{ transform: activePress ? 'translateY(2px)' : 'none' }}
      >
        {/* Shadow layer at bottom */}
        <div className="absolute inset-0 top-[8px]" style={{ background: colors.shadow }} />
        {/* Gradient layer on top */}
        <div
          className="absolute inset-0 bottom-[8px] rounded-b-[16px]"
          style={{
            background: `linear-gradient(to top, ${colors.gradient.start}, ${colors.gradient.middle} 50%, ${colors.gradient.end})`
          }}
        />
      </div>

      {/* Content */}
      <div
        className="relative size-full flex flex-col items-center pt-[3%] pointer-events-none transition-transform duration-150"
        style={{ transform: activePress ? 'translateY(2px)' : 'none' }}
      >
        <TitlePill title={title} variant={variant} />
        {subtitle && (
          <div className="flex-1 flex items-start justify-center pt-[2%]">
            <div className="inline-flex items-center gap-1 whitespace-nowrap">
              <span
                className="font-mind-boggle text-[#F2F2F2] leading-none"
                style={{
                  fontSize: 'clamp(1rem, 8cqw, 2.5rem)',
                  fontWeight: 1000,
                  ...createResponsiveTextStroke(1, 0.05)
                }}
              >
                {subtitle}
              </span>
              {subtitleIcon && (
                <span
                  className="inline-block flex-shrink-0"
                  style={{
                    height: 'clamp(1rem, 8cqw, 2.5rem)',
                    width: 'clamp(1rem, 8cqw, 2.5rem)'
                  }}
                >
                  {subtitleIcon}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </button>
  );
}

export function GameModeCard({
  variant = 'blue',
  onSkinsClick,
  showNewBadge,
  title,
  subtitle,
  subtitleIcon,
  centerButtonProps,
  rightContent,
  rightButtonProps,
  showArrow = true,
  className = ''
}: GameModeCardProps) {
  return (
    <div
      className={`relative bg-black/40 backdrop-blur-md rounded-[20px] p-1 flex gap-2 border border-white/10 ${className}`}
      style={{ containerType: 'inline-size' }}
    >
      {/* Left Button Section - SkinsButton */}
      <div className="relative w-[22%] aspect-square flex-none">
        <SkinsButton className="size-full" onClick={onSkinsClick} showNewBadge={showNewBadge} />
      </div>

      {/* Center Button Section - Custom wide button */}
      <CenterButton
        variant={variant}
        title={title}
        subtitle={subtitle}
        subtitleIcon={subtitleIcon}
        {...centerButtonProps}
      />

      {/* Right Button Section */}
      <Button variant={variant} className="w-[22%] aspect-square flex-none" {...rightButtonProps}>
        <div className="relative size-full flex items-center justify-center p-[10%]">
          {showArrow && <ArrowIndicator color={variantColors[variant].gradient.middle} />}
          {rightContent}
        </div>
      </Button>
    </div>
  );
}

export default GameModeCard;
